import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTabContext } from "@/contexts/TabContext";
import { useTabTitleSync } from "@/hooks/useTabTitleSync";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useSplitViewAwareParams } from '@/hooks/useSplitViewAwareParams';
import { useSplitViewContext } from  '@/components/layout/SplitViewContent';
import { useDocumentDataContext } from "@/providers/DocumentDataProvider";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";

import {
  useToast,
  useDocumentBlocks,
  useWidgetData,
  usePageLayout,
} from "@/hooks";
import { useContentHighlighting } from "@/hooks/useContentHighlighting";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";
import { DocumentWriterEditor } from "@/components/document-writer/DocumentWriterEditor";
import { Block } from "@/components/editor/Editor";
import { generateId } from "@/lib/utils/uuid";

// Types
import { NoConfigMessage } from "@/components/ui/LoadingStates";  
import { extractBlockData } from "@/lib/utils/blockUtils";
import { DocumentData } from "@/lib/stores/documentStore";
import { useItemLock } from "@/hooks/useItemLock";
import { LockStatusBanner } from "@/components/ui/LockStatusBanner";
import { UnifiedWizardHeader } from "@/components/wizards/shared/UnifiedWizardHeader";
import { useWizardDraft, DocumentDraftData } from "@/hooks/useWizardDraft";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
// Main component
export default function DocumentDetailPage() {
  const { t } = usePlatformTranslation();
  const { documentId } = useSplitViewAwareParams<{ documentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { selectedGame, getUserNameById } = useSimplifiedUserData();
  
  // Get folder from URL params for new documents
  const folderFromUrl = searchParams.get('folder');
  const { updateTabTitle, updateTabUnsavedChanges, updateTabCleanup, tabs, requestTabClose } = useTabContext();
  const splitViewContext = useSplitViewContext();
  const isSplitView = splitViewContext?.isSplitView ?? false;
  const {
    documents,
    isLoading: documentsLoading,
    updateDocument,
    createDocument,
  } = useDocumentDataContext();

  const [isSaving, setIsSaving] = useState(false);
  const [wizardMode, setWizardMode] = useState<'edit' | 'view'>('edit');
  const [showDiscardChangesDialog, setShowDiscardChangesDialog] = useState(false);
  const hasRestoredDraftRef = useRef(false);
  
  // Track lock release function for explicit tab close
  const lockReleaseRef = useRef<(() => Promise<void>) | undefined>();
  
  // Track save handler for auto-save
  const saveHandlerRef = useRef<(() => Promise<void>) | undefined>();
  
  // Track user activity for smart auto-save (don't save while typing)
  const [lastActivityTimestamp, setLastActivityTimestamp] = useState<number>(0);
  
  // Check if this is a new document creation
  const isNewDocument = documentId === 'new';
  
  // State for new document
  const [newDocumentName, setNewDocumentName] = useState('New Document');
  const [newDocumentBlocks, setNewDocumentBlocks] = useState<Block[]>([
    { id: generateId(), type: 'text', content: '' }
  ]);

  // Draft persistence for tab switching
  const {
    draftData,
    hasDraftData,
    saveDraftData,
    clearDraft,
  } = useWizardDraft<DocumentDraftData>({
    draftType: 'document',
    gameId: selectedGame?.id,
    itemId: isNewDocument ? undefined : documentId,
    enabled: true,
  });
  
  // Restore draft data for new documents
  useEffect(() => {
    if (isNewDocument && hasDraftData && draftData && !hasRestoredDraftRef.current) {
      console.log('[DocumentDetailPage] Restoring draft for new document');
      hasRestoredDraftRef.current = true;
      if (draftData.name) setNewDocumentName(draftData.name);
      if (draftData.blocks && draftData.blocks.length > 0) {
        setNewDocumentBlocks(draftData.blocks as Block[]);
      }
    }
  }, [isNewDocument, hasDraftData, draftData]);

  // Item lock system for collaborative editing
  const {
    lockStatus,
    canEdit,
    isAcquiring: isAcquiringLock,
    lockError,
    isLockedByOther,
    refreshLockStatus,
    forceLock,
    releaseImmediately,
  } = useItemLock({
    itemId: isNewDocument ? undefined : documentId, // Don't lock new documents (they don't exist yet)
    itemType: 'document',
    gameId: selectedGame?.id,
    autoAcquire: !isNewDocument, // Don't auto-acquire lock for new documents
    isEditMode: true, // Documents are always in edit mode
    onLockLost: () => {
      toast({
        title: 'Edit lock lost',
        description: 'Another user has taken over editing. Your changes may not be saved.',
        variant: 'destructive',
      });
    },
  });
  
  // Store lock release function in ref for cleanup handler
  useEffect(() => {
    lockReleaseRef.current = releaseImmediately;
  }, [releaseImmediately]);
  
  // Register lock release cleanup handler when lock release function is ready
  useEffect(() => {
    if (!lockReleaseRef.current) return;
    
    // Find the ACTUAL tab ID by looking for the active tab
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) return;
    
    const tabId = activeTab.id;
    
    // Register the lock release function to be called before tab closes
    updateTabCleanup(tabId, lockReleaseRef.current);
    
    return () => {
      // Clean up the handler when component unmounts
      updateTabCleanup(tabId, undefined);
    };
  }, [tabs, location.pathname, updateTabCleanup]);
  
  // Track highlights in the document content
  const { trackFromData } = useContentHighlighting();

  // Find the document by ID - include documentId in the key to force fresh lookup
  const document = useMemo(() => {
    if (!documentId || !documents) return null;
    return documents.find((doc) => doc.id === documentId);
  }, [documentId, documents]);
  
  // Sync tab title with document name (handles both initial load and name changes)
  // Skip in split view mode - split view tabs are managed separately
  // For new documents, use newDocumentName; for existing documents, use document.name
  useTabTitleSync(isNewDocument ? newDocumentName : document?.name, { disabled: isSplitView });

  // Redirect to home if document not found after loading completes (skip for new documents)
  useEffect(() => {
    if (!isNewDocument && !documentsLoading && documentId && !document) {
      console.warn(`Document ${documentId} not found or access denied. Redirecting to home.`);
      navigate('/home', { replace: true });
    }
  }, [document, documentId, documentsLoading, navigate, isNewDocument]);

  // Custom hooks
  const { blocks, hasChanges, loadBlocks, updateBlocks, markAsSaved, revertToSaved } =
    useDocumentBlocks(document);
  const { updateWidgetData, getWidgetDataObject } = useWidgetData();

  // Load document data when document changes, restoring from draft if available
  useEffect(() => {
    if (document) {
      // Check if we have draft data to restore
      if (hasDraftData && draftData && draftData.blocks && draftData.blocks.length > 0 && !hasRestoredDraftRef.current) {
        console.log('[DocumentDetailPage] Restoring draft data for document:', documentId);
        hasRestoredDraftRef.current = true;
        // Restore blocks from draft
        updateBlocks(draftData.blocks as Block[]);
      } else if (!hasRestoredDraftRef.current) {
        // No draft, load from document
        loadBlocks(document);
        hasRestoredDraftRef.current = true;
      }
    }
  }, [document, loadBlocks, hasDraftData, draftData, documentId, updateBlocks]);

  // Reset draft restoration flag when document changes
  useEffect(() => {
    hasRestoredDraftRef.current = false;
  }, [documentId]);

  // Save draft when blocks change (for persistence across tab switches)
  useEffect(() => {
    if (document && hasChanges && selectedGame?.id) {
      const currentDraftData: DocumentDraftData = {
        name: document.name,
        blocks: blocks,
        widgetData: getWidgetDataObject(),
        tags: document.tags || [],
        settings: document.settings || {},
      };
      // Defer the save to avoid state updates during render
      queueMicrotask(() => {
        saveDraftData(currentDraftData);
      });
    }
  }, [blocks, hasChanges, document, selectedGame?.id, saveDraftData, getWidgetDataObject]);

  // Tab title sync is now handled by useTabTitleSync hook above

  // Track highlights in the document blocks
  // We use a combination of blocks length and content to trigger re-tracking
  const blocksContentSignature = blocks ? blocks.map(b => b.content).join('|') : '';
  
  useEffect(() => {
    if (blocks && blocks.length > 0) {
      // Extract content from all blocks
      const blockContents = blocks
        .map(block => block.content)
        .filter(Boolean);
      
      // Debounce the tracking update
      const timeoutId = setTimeout(() => {
        trackFromData({ blocks: blockContents });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocksContentSignature, blocks]);

  // Event handlers
  const handleObjectCreated = useCallback(
    (documentId: string) => {
      toast({
        title: "Document Created",
        description:
          'The document has been created and added to this document. Click "Save Changes" to save.',
      });
    },
    [toast]
  );

  const handleDocumentWriterChange = useCallback(
    (blocks: Block[]) => {
      updateBlocks(blocks);
      // Track activity for smart auto-save
      setLastActivityTimestamp(Date.now());
    },
    [updateBlocks]
  );

  const handleWidgetDataChange = useCallback(
    (
      widgetId: string,
      glossaryItemId: string,
      data: Record<string, unknown>
    ) => {
      updateWidgetData(widgetId, glossaryItemId, data);
    },
    [updateWidgetData]
  );

  const handleGlossaryItemEdit = useCallback(
    (glossaryItemId: string) => {
      navigate(`/library/${glossaryItemId}?edit=true`);
    },
    [navigate]
  );

  const handleDocumentDisplayEdit = useCallback(
    (documentId: string) => {
      navigate(`/documents/${documentId}?edit=true`);
    },
    [navigate]
  );

  const handleDocumentDisplayNavigate = useCallback(
    (documentId: string) => {
      navigate(`/documents/${documentId}`);
    },
    [navigate]
  );

  const handleDocumentBlockDelete = useCallback(
    (blockId: string) => {
      console.log("Removing block from document:", blockId);
      
      // Remove the block from the blocks array
      const updatedBlocks = blocks.filter((block) => block.id !== blockId);
      updateBlocks(updatedBlocks);
      
      // Save the document with the updated blocks
      setTimeout(() => {
        const saveWithRemovedBlock = async () => {
          if (!document || !selectedGame) return;
          
          setIsSaving(true);
          try {
            const documentData: DocumentData = {
              blocks: extractBlockData(updatedBlocks),
              widgetData: getWidgetDataObject(),
              lastSaved: new Date().toISOString(),
            };
            
            await updateDocument({
              id: document.id,
              game_id: selectedGame.id,
              name: document.name,
              data: documentData as unknown as DocumentData[],
              settings: document.settings,
              tags: document.tags,
            });

            markAsSaved();
            clearDraft(); // Clear draft after successful save
            toast({
              title: "Success",
              description: "Block removed and document saved successfully",
            });
            
          } catch (error) {
            console.error("Error saving document after block removal:", error);
            toast({
              title: "Error",
              description: "Failed to save document after block removal",
              variant: "destructive",
            });
          } finally {
            setIsSaving(false);
          }
        };
        
        saveWithRemovedBlock();
      }, 100);
    },
    [blocks, updateBlocks, document, selectedGame, getWidgetDataObject, updateDocument, markAsSaved, toast, setIsSaving, clearDraft]
  );

  const handleDocumentBlockCancel = useCallback(
    (blockId: string) => {
      console.log("Canceling unsaved block:", blockId);
      
      // Remove the unsaved block from the blocks array
      const updatedBlocks = blocks.filter((block) => block.id !== blockId);
      updateBlocks(updatedBlocks);
      
      // Ensure there's always at least one block and the last block is a text block
      if (updatedBlocks.length === 0) {
        updatedBlocks.push({
          id: generateId(),
          type: 'text',
          content: ''
        });
        updateBlocks(updatedBlocks);
      } else {
        const lastBlock = updatedBlocks[updatedBlocks.length - 1];
        if (lastBlock.type !== 'text') {
          updatedBlocks.push({
            id: generateId(),
            type: 'text',
            content: ''
          });
          updateBlocks(updatedBlocks);
        }
      }
      
      // Save the document with the updated blocks
      setTimeout(() => {
        const saveWithCanceledBlock = async () => {
          if (!document || !selectedGame) return;
          
          setIsSaving(true);
          try {
            const documentData: DocumentData = {
              blocks: extractBlockData(updatedBlocks),
              widgetData: getWidgetDataObject(),
              lastSaved: new Date().toISOString(),
            };
            
            await updateDocument({
              id: document.id,
              game_id: selectedGame.id,
              name: document.name,
              data: documentData as unknown as DocumentData[],
              settings: document.settings,
              tags: document.tags,
            });

            markAsSaved();
            clearDraft(); // Clear draft after successful save
            toast({
              title: "Success",
              description: "Unsaved block removed and document saved",
            });
            
          } catch (error) {
            console.error("Error saving document after block cancellation:", error);
            toast({
              title: "Error",
              description: "Failed to save document after block cancellation",
              variant: "destructive",
            });
          } finally {
            setIsSaving(false);
          }
        };
        
        saveWithCanceledBlock();
      }, 100);
    },
    [blocks, updateBlocks, document, selectedGame, getWidgetDataObject, updateDocument, markAsSaved, toast, setIsSaving, clearDraft]
  );

  const handleDocumentSave = useCallback(async () => {
    // This is called by the DocumentBlock after creating/updating a glossary item
    // We need to save the document to embed the glossary item reference
    if (!document || !selectedGame) return;
    
    try {
      const documentData: DocumentData = {
        blocks: extractBlockData(blocks),
        widgetData: getWidgetDataObject(),
        lastSaved: new Date().toISOString(),
      };
      
      
      await updateDocument({
        id: document.id,
        game_id: selectedGame.id,
        name: document.name,
            data: documentData as unknown as DocumentData[],
        settings: document.settings,
        tags: document.tags,
      });

      markAsSaved();
      clearDraft(); // Clear draft after successful save
      toast({
        title: "Success",
        description: "Document saved with embedded glossary item",
      });
    } catch (error) {
      console.error("Error saving document with glossary item:", error);
      toast({
        title: "Error",
        description: "Failed to save document with glossary item",
        variant: "destructive",
      });
    }
  }, [document, selectedGame, blocks, getWidgetDataObject, updateDocument, markAsSaved, toast, clearDraft]);

  const handleDocumentContentUpdate = useCallback(
    (blockId: string, glossaryItemId: string, glossaryItemName: string) => {
      
      const updatedBlocks = blocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              content: glossaryItemId,
              metadata: {
                glossaryItemName,
                glossaryItemId,
                blockType: "glossary-reference",
              },
            }
          : block
      );
      
      
      // Update the blocks in DocumentDetailPage state
      updateBlocks(updatedBlocks);
      
      // Use the updatedBlocks directly instead of relying on the state update
      // This ensures we save the correct blocks immediately
      setTimeout(() => {
        
        // Create a temporary function that uses the updatedBlocks
        const saveWithUpdatedBlocks = async () => {
          if (!document || !selectedGame) return;
          
          
          setIsSaving(true);
          try {
            const documentData: DocumentData = {
              blocks: extractBlockData(updatedBlocks),
              widgetData: getWidgetDataObject(),
              lastSaved: new Date().toISOString(),
            };
                        
            await updateDocument({
              id: document.id,
              game_id: selectedGame.id,
              name: document.name,
              data: documentData as unknown as DocumentData[],
              settings: document.settings,
              tags: document.tags,
            });

            markAsSaved();
            clearDraft(); // Clear draft after successful save
            toast({
              title: "Success",
              description: "Document content saved successfully",
            });
            
          } catch (error) {
            console.error("Error saving document content:", error);
            toast({
              title: "Error",
              description: "Failed to save document content",
              variant: "destructive",
            });
          } finally {
            setIsSaving(false);
          }
        };
        
        saveWithUpdatedBlocks();
      }, 100); // Small delay to ensure updateBlocks has completed
    },
    [blocks, updateBlocks, document, selectedGame, getWidgetDataObject, updateDocument, markAsSaved, toast, setIsSaving, clearDraft]
  );

  // Handler for creating a new document
  const handleCreateNewDocument = useCallback(async () => {
    if (!selectedGame) return;

    setIsSaving(true);
    try {
      const blockData = extractBlockData(newDocumentBlocks);
      
      const documentData: DocumentData = {
        blocks: blockData,
        widgetData: {},
        lastSaved: new Date().toISOString(),
      };
      
      const newDocument = await createDocument({
        game_id: selectedGame.id,
        name: newDocumentName.trim() || 'New Document',
        data: documentData as unknown as DocumentData[],
        settings: {},
        tags: [],
        folder: folderFromUrl || undefined, // Include folder from URL params
      });

      clearDraft(); // Clear draft after successful save
      toast({
        title: "Success",
        description: "Document created successfully",
      });
      
      // Clear tab unsaved changes BEFORE navigation to prevent stale hasUnsavedChanges
      // from persisting through the component remount (key change causes old editor to unmount,
      // losing its cleanup, while the tab retains the old unsaved changes flag)
      const activeTab = tabs.find(t => t.isActive);
      if (activeTab) {
        updateTabUnsavedChanges(activeTab.id, false);
      }
      
      // Navigate to the newly created document
      navigate(`/documents/${newDocument.id}`, { replace: true });
      
    } catch (error) {
      console.error("Error creating document:", error);
      toast({
        title: "Error",
        description: "Failed to create document",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedGame, newDocumentBlocks, newDocumentName, createDocument, clearDraft, toast, navigate, folderFromUrl, tabs, updateTabUnsavedChanges]);

  // Handle closing the document (will show save/discard/cancel dialog if there are unsaved changes)
  const handleClose = useCallback(async () => {
    // Find the ACTUAL tab ID by looking for the active tab
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) {
      navigate('/documents');
      return;
    }
    
    const tabId = activeTab.id;
    
    // Request to close the tab (will show unsaved changes dialog if needed)
    const canClose = await requestTabClose(tabId);
    
    if (canClose) {
      // If the tab was closed, navigate to documents page
      navigate('/documents');
    }
  }, [tabs, requestTabClose, navigate]);

  const handleSaveDocumentWriter = useCallback(async () => {
    if (!document || !selectedGame) return;
    // #region agent log
    if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DocumentDetailPage.tsx:handleSaveDocumentWriter',message:'SAVE TRIGGERED',data:{blockCount:blocks?.length,hasChanges,callerStack:new Error().stack?.split('\n').slice(1,4).join(' | ')},timestamp:Date.now(),hypothesisId:'H-D'})}).catch(()=>{});}
    // #endregion
    setIsSaving(true);
    try {
      const blockData = extractBlockData(blocks);
      
      // Log what we're about to save to verify highlights are included
      console.log('💾 Saving document with blocks:', {
        blockCount: blockData.length,
        blocksWithHighlights: blockData.filter(b => b.content?.includes('data-highlight')).length,
        sample: blockData[0]?.content?.substring(0, 200)
      });

      const documentData: DocumentData = {
        blocks: blockData,
        widgetData: getWidgetDataObject(),
        lastSaved: new Date().toISOString(),
      };
      
      
      await updateDocument({
        id: document.id,
        game_id: selectedGame.id,
        name: document.name,
        data: documentData as unknown as DocumentData[],
        settings: document.settings,
        tags: document.tags,
      });

      markAsSaved();
      clearDraft(); // Clear draft after successful save
      toast({
        title: "Success",
        description: "Document content saved successfully",
      });
      
    } catch (error) {
      console.error("Error saving document content:", error);
      toast({
        title: "Error",
        description: "Failed to save document content",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [document, selectedGame, blocks, getWidgetDataObject, updateDocument, markAsSaved, toast, clearDraft]);


  // Update tab unsaved changes state and track save handler for auto-save
  useEffect(() => {
    if (documentId) {
      // Find the ACTUAL tab ID by looking for the active tab
      // Tab IDs may be stable IDs like 'tab-documents' rather than the pathname
      const activeTab = tabs.find(t => t.isActive);
      if (!activeTab) return;
      
      const tabId = activeTab.id;
      
      // Create discard handler that clears the draft
      const discardHandler = () => {
        clearDraft();
      };
      
      if (isNewDocument) {
        // For new documents, track changes based on content
        const hasNewDocChanges = newDocumentBlocks.some(b => b.content && b.content.trim() !== '') || 
                                  newDocumentName !== 'New Document';
        updateTabUnsavedChanges(tabId, hasNewDocChanges, handleCreateNewDocument, discardHandler);
      } else {
        // Store save handler ref for auto-save
        saveHandlerRef.current = handleSaveDocumentWriter;
        updateTabUnsavedChanges(tabId, hasChanges, handleSaveDocumentWriter, discardHandler);
      }
    }
  }, [documentId, tabs, hasChanges, isNewDocument, newDocumentBlocks, newDocumentName, updateTabUnsavedChanges, handleSaveDocumentWriter, handleCreateNewDocument, clearDraft]);
  
  // Enable auto-save based on user settings (only for existing documents)
  // Smart auto-save: waits for user to stop typing (3 seconds of inactivity) before saving
  useAutoSave({
    hasUnsavedChanges: hasChanges,
    saveHandler: saveHandlerRef.current,
    enabled: !isNewDocument && canEdit, // Only auto-save when we have the lock
    activityTimestamp: lastActivityTimestamp, // Track user activity to avoid saving while typing
    idleDelaySeconds: 3, // Wait 3 seconds after last activity before auto-saving
  });
  
  // Save draft for new documents when content changes
  useEffect(() => {
    if (isNewDocument && selectedGame?.id) {
      const hasContent = newDocumentBlocks.some(b => b.content && b.content.trim() !== '') || 
                         newDocumentName !== 'New Document';
      if (hasContent) {
        // Defer the save to avoid state updates during render
        queueMicrotask(() => {
          saveDraftData({
            name: newDocumentName,
            blocks: newDocumentBlocks,
            widgetData: {},
            tags: [],
            settings: {},
          });
        });
      }
    }
  }, [isNewDocument, newDocumentBlocks, newDocumentName, selectedGame?.id, saveDraftData]);

  const handleTitleChange = async (newTitle: string) => {
    if (newTitle.trim() === "" || newTitle.trim() === document.name) {
      return;
    }

    try {
      await updateDocument({
        id: document.id,
        game_id: selectedGame.id,
        name: newTitle.trim(),
        data: document.data as unknown as DocumentData[],
        settings: document.settings,
        tags: document.tags,
      });
      
      // Tab title update is handled by useTabTitleSync hook
      
      toast({
        title: "Success",
        description: "Document title updated successfully",
      });
    } catch (error) {
      console.error("Error updating document title:", error);
      toast({
        title: "Error",
        description: "Failed to update document title",
        variant: "destructive",
      });
    }
  };

  const handleEdit = () => {
    setWizardMode('edit');
  };

  const handleFormCancel = useCallback(() => {
    // If there are unsaved changes, show the discard confirmation dialog
    if (hasChanges) {
      setShowDiscardChangesDialog(true);
    }
    // If no changes, do nothing - documents are always in edit mode
    // There's nothing to cancel without unsaved changes
  }, [hasChanges]);

  const handleDiscardChanges = useCallback(() => {
    // Revert to the last saved version (the baseline set when document was saved)
    revertToSaved();
    clearDraft(); // Clear the draft since we're discarding changes
    setShowDiscardChangesDialog(false);
    // Stay in edit mode - user just wants to revert to saved version, not leave editing
    toast({
      title: 'Changes Discarded',
      description: 'The document has been reverted to the last saved version.',
    });
  }, [revertToSaved, clearDraft, toast]);

  const handleKeepEditing = useCallback(() => {
    // Close the dialog and keep the changes
    setShowDiscardChangesDialog(false);
  }, []);

  // Page layout configuration
  const pageLayoutConfig = usePageLayout(
    document,
    handleObjectCreated,
    handleWidgetDataChange
  );

  // Loading state (skip for new documents)
  if (!isNewDocument && documentsLoading) {
    return <LoadingSpinner size="lg" text="Loading document..." />;
  }

  // For new documents, check if we have the required game
  if (isNewDocument) {
    if (!selectedGame?.id) {
      return <LoadingSpinner size="lg" text="Loading..." />;
    }
    
    const hasNewDocChanges = newDocumentBlocks.some(b => b.content && b.content.trim() !== '') || 
                              newDocumentName !== 'New Document';
    
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Document Header */}
        <div className="bg-surface border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <UnifiedWizardHeader
              title={newDocumentName}
              mode="edit"
              itemType="document"
              onTitleChange={setNewDocumentName}
              onCancel={handleClose}
              onSubmit={handleCreateNewDocument}
              isSubmitting={isSaving}
              hasUnsavedChanges={hasNewDocChanges}
              submitDisabled={!hasNewDocChanges}
              submitText="Create Document"
            />
          </div>
        </div>

        {/* Document Content */}
        <div className="flex-1 overflow-y-auto">
          <DocumentWriterEditor
            initialBlocks={newDocumentBlocks}
            onChange={setNewDocumentBlocks}
            className="h-full"
            gameId={selectedGame?.id}
          />
        </div>
      </div>
    );
  }

  // Error state - document not found
  if (!document) {
    return <NoConfigMessage message="The document you're looking for doesn't exist or has been deleted." />;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Lock Status Banner */}
      {isLockedByOther && (
        <div className="px-6 py-4 max-w-4xl mx-auto w-full">
          <LockStatusBanner
            lockStatus={lockStatus}
            isAcquiring={isAcquiringLock}
            lockError={lockError}
            canEdit={canEdit}
            onRefresh={refreshLockStatus}
            onForceTakeover={async () => {
              const success = await forceLock();
              if (success) {
                toast({
                  title: 'Edit access acquired',
                  description: 'You now have edit access to this document.',
                });
              }
            }}
            canForceTakeover={true}
            size="md"
          />
        </div>
      )}

      {/* Document Header */}
      <div className="bg-surface border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <UnifiedWizardHeader
            title={document.name}
            mode={wizardMode}
            itemId={documentId}
            itemType="document"
            onTitleChange={handleTitleChange}
            onCancel={handleFormCancel}
            onSubmit={handleSaveDocumentWriter}
            onEdit={handleEdit}
            isSubmitting={isSaving}
            hasUnsavedChanges={hasChanges}
            submitDisabled={!hasChanges}
            submitText="Save Changes"
            tags={document.tags || []}
            owner={document.created_by}
            canEdit={canEdit}
            status={document.status}
          />
        </div>
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-y-auto">
        <DocumentWriterEditor
          initialBlocks={blocks}
          onChange={handleDocumentWriterChange}
          className="h-full"
          gameId={selectedGame?.id}
          editable={canEdit}
          onDocumentCreated={handleObjectCreated}
          onGlossaryItemEdit={handleGlossaryItemEdit}
          onDocumentDisplayEdit={handleDocumentDisplayEdit}
          onDocumentDisplayNavigate={handleDocumentDisplayNavigate}
          onDocumentContentUpdate={handleDocumentContentUpdate}
          onDocumentSave={handleDocumentSave}
          onDocumentBlockDelete={handleDocumentBlockDelete}
          onDocumentBlockCancel={handleDocumentBlockCancel}
        />
      </div>

      {/* Discard Changes Confirmation Dialog */}
      <AlertDialog open={showDiscardChangesDialog} onOpenChange={setShowDiscardChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you discard, your changes will be lost and the document will revert to the last saved version.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={handleKeepEditing}
            >
              Keep Editing
            </Button>
            <Button
              variant="destructive"
              onClick={handleDiscardChanges}
            >
              Discard Changes
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
